import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, merge, Subject } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { DelegationService } from 'src/app/data/delegation.service';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DelegationAddInvoiceFormComponent } from '../settle-delegation-form/delegation-cost-step/delegation-add-invoice-form/delegation-add-invoice-form.component';
import { DelegationInvoiceListDataSource } from './delegation-invoice-list.datasource';
import { DocumentViewerService } from '../../shared/services/document-viewer.service';
import { FileRequest } from 'src/app/models/FileRequest';
import { CustomMultipleFileViewerComponent } from '../../shared/components/custom-multiple-file-viewer/custom-multiple-file-viewer.component';

@Component({
  selector: 'app-delegation-invoice-list',
  templateUrl: './delegation-invoice-list.component.html',
  styleUrls: ['./delegation-invoice-list.component.scss'],
  standalone: false
})
export class DelegationInvoiceListComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource = inject(DelegationInvoiceListDataSource);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private delegationService = inject(DelegationService);

  @Input() delegationId: number;
  @Input() canEdit: boolean;

  displayedColumns = [
    'invoiceNumber',
    'description',
    'delegationInvoiceTypeName',
    'grossValue',
    'currencyName',
    'paymentTypeName',
    'issueDate',
    'actions',
  ];

  delegationFormGroup: UntypedFormGroup;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private readonly defaultPage: number = 1;
  private readonly defaultPageSize: number = 10;
  private readonly defaultSortColumn: string = 'invoiceNumber';
  private readonly defaultSortDirection: string = 'desc';

  private readonly unsubscribe$ = new Subject<void>();

  constructor() {
  }

  ngOnInit(): void {
    this.dataSource.delegationsInvoicesSubject.next({
      DelegationId: this.delegationId,
      Page: this.defaultPage,
      Count: this.defaultPageSize,
      SortingField: this.defaultSortColumn,
      SortingDirection: this.defaultSortDirection
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchDelegationInvoices()))
      .subscribe();

    this.fetchDelegationInvoices();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  openDetailsModal(element) {
    this.dialog
      .open(DelegationAddInvoiceFormComponent, { data: { delegationId: this.delegationId, record: element, isEdit: false }, panelClass: 'delegation-invoice-dialog' })
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
      });
  }

  openEditModal(element) {
    this.dialog
      .open(DelegationAddInvoiceFormComponent, { data: { delegationId: this.delegationId, record: element, isEdit: true }, panelClass: 'delegation-invoice-dialog' })
      .afterClosed()
      .pipe(first())
      .subscribe(() => {
      });
  }

  deleteDelegationCost(delegationId: number, delegationCostId: number) {
    const onConfirm = (delegationId, delegationCostId) => {
      this.spinner.show();
      return this.delegationService.deleteDelegationInvoice(delegationId, delegationCostId).pipe(
        tap((_) => this.fetchDelegationInvoices()),
        tap((_) => this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeletedDelegationInvoice)),
        finalize(() => this.spinner.hide()),
      );
    };

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmDeletingDelegationInvoiceMessage } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? onConfirm(delegationId, delegationCostId) : EMPTY)),
      )
      .subscribe();
  }

  openFilesModal(element: any) {
    const files: FileRequest[] = element.Files.map(file => ({
      Endpoint: `delegations/delegationCost/${element.DelegationCostId}/delegationCostFile`,
      FileId: file.Id,
      FileName: file.FileName,
    }));

    this.dialog.open(CustomMultipleFileViewerComponent, {
      data: files
    });
  }

  private fetchDelegationInvoices() {
    this.dataSource.delegationsInvoicesSubject.next({
      DelegationId: this.delegationId,
      Page: this.paginator.pageIndex + 1,
      Count: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
    });
  }
}
